import { Link } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import FooterSection from '../../components/FooterSection';
import { WA_NUMBER } from '../../constant/detail';

const relatedServices = [
  { label: 'Jasa Maintenance Kolam Renang Bali', to: '/jasa-maintenance-kolam-renang-bali' },
  { label: 'Pasang Filter Kolam Renang Bali', to: '/pasang-filter-kolam-renang-bali' },
  { label: 'Jasa Testkit Air Kolam Bali', to: '/jasa-testkit-air-kolam-bali' },
  { label: 'Jual Equipment Pool Bali', to: '/equipment-pool-bali' },
  { label: 'Konsultasi Kolam Renang Bali', to: '/konsultasi-kolam-renang-bali' },
];

const WA_MESSAGE = encodeURIComponent(
  'Hello, I would like to order pool chemicals in Bali'
);

const JualChemicalPool = () => {
  return (
    <>
      <Head>
        <title>Jual Chemical Pool Bali | Klorin, HCl, Tawas | Surya Jaya Teknik</title>
        <meta
          name="description"
          content="Jual chemical pool Bali: klorin, pH minus/plus, algaecide, tawas. Harga terbaik, stok lengkap, pengiriman cepat se-Bali. Konsultasi dosis gratis. Order sekarang!"
        />
        <link rel="canonical" href="https://poolshopsuryajayateknik.com/jual-chemical-pool-bali" />
        <meta property="og:title" content="Jual Chemical Pool Bali | Klorin, HCl, Tawas | Surya Jaya Teknik" />
        <meta property="og:description" content="Jual chemical pool Bali: klorin, pH minus/plus, algaecide, tawas. Harga terbaik, stok lengkap, pengiriman cepat se-Bali." />
        <meta property="og:image" content="https://poolshopsuryajayateknik.com/img/blog/img-2.jpg" />
        <meta property="og:url" content="https://poolshopsuryajayateknik.com/jual-chemical-pool-bali" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Jual Chemical Pool Bali | Klorin, HCl, Tawas | Surya Jaya Teknik" />
        <meta name="twitter:description" content="Jual chemical pool Bali: klorin, pH minus/plus, algaecide, tawas. Harga terbaik, stok lengkap, pengiriman cepat se-Bali." />
      </Head>

      <main className="mt-16 bg-white text-gray-800">
        {/* Hero */}
        <section className="bg-tertiary text-white py-16 px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
              Pool Chemicals Bali – Complete, Original &amp; Best Price
            </h1>
            <p className="text-lg text-primary/80 mb-8">
              Looking for quality and trusted pool chemicals in Bali? Suryajaya
              Teknik supplies a full range of pool chemicals including chlorine,
              pH adjusters, algaecide, and flocculant at competitive prices with
              fast delivery across Bali. All our products are tested and safe
              for private pools, villas, and hotels. We also provide
              consultation to help you determine the right dosage for your pool
              size and condition.
            </p>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary hover:opacity-90 text-tertiary font-semibold py-3 px-8 rounded-full text-lg transition"
            >
              Order via WhatsApp Now
            </a>
          </div>
        </section>

        {/* Why */}
        <section className="max-w-4xl mx-auto py-14 px-6">
          <h2 className="text-2xl font-bold text-tertiary mb-4">
            Why the Right Pool Chemical Matters
          </h2>
          <p className="mb-4 leading-relaxed">
            Using the wrong type or dosage of pool chemicals can cause serious
            problems for your pool. Water that is too acidic can damage pool
            materials such as plaster, tiles, and metal equipment in a short
            time. On the other hand, water that is too alkaline causes
            cloudiness and makes chlorine ineffective at killing bacteria. In
            Bali, the hot and humid weather accelerates algae and bacteria
            growth, making the right chemical selection and dosage absolutely
            critical. That is why choosing a trusted and experienced pool
            chemical supplier is an important investment for pool owners.
          </p>
          <p className="leading-relaxed">
            As an experienced pool chemical distributor in Bali, we ensure every
            product we sell is original with a standardized active
            concentration. We do not just sell products - we also provide proper
            usage guidance so the chemicals work optimally in your pool. Our
            customers include private pool owners, villa managers, five-star
            hotels, and pool contractors across Bali. By buying from us, you get
            product authenticity, transparent pricing, and technical support you
            will not find at a regular shop.
          </p>
        </section>

        {/* Products */}
        <section className="bg-primary/10 py-14 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-tertiary mb-6">
              Pool Chemicals We Supply
            </h2>
            <div className="grid md:grid-cols-2 gap-5">
              {[
                {
                  name: 'Granular / Tablet Chlorine (TCCA 90%)',
                  desc: 'The main pool disinfectant to kill bacteria, viruses, and algae. Available in 1 kg, 5 kg, and 25 kg packaging.',
                },
                {
                  name: 'pH Minus (Sodium Bisulfate)',
                  desc: 'Lowers pool water pH that is too high (alkaline). Safe and easy to use, suitable for all types of pools.',
                },
                {
                  name: 'pH Plus (Sodium Carbonate)',
                  desc: 'Raises pool water pH that is too low (acidic). Protects the pool from corrosion caused by overly acidic water.',
                },
                {
                  name: 'Algaecide (Anti-Algae)',
                  desc: 'Prevents and eliminates algae growth on pool walls and floor. Special formula that does not cause excessive foam.',
                },
                {
                  name: 'Flocculant / Clarifier',
                  desc: 'Clumps fine particles that cause cloudy water so they can be filtered out easily. Pool water becomes clear within 24 hours.',
                },
                {
                  name: 'Shock Treatment (Non-Chlorine)',
                  desc: 'A powerful oxidizer to treat very dirty pool water or after heavy use. Safe for sensitive skin.',
                },
              ].map((prod, i) => (
                <div
                  key={i}
                  className="bg-white border border-primary rounded-xl p-5 shadow-sm"
                >
                  <h3 className="font-semibold text-tertiary mb-2">
                    {prod.name}
                  </h3>
                  <p className="text-gray-600 text-sm">{prod.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Us */}
        <section className="max-w-4xl mx-auto py-14 px-6">
          <h2 className="text-2xl font-bold text-tertiary mb-6">
            Why Buy Pool Chemicals from Suryajaya Teknik?
          </h2>
          <ul className="space-y-3">
            {[
              'Original products meeting international standards - no diluted or fake products',
              'Competitive pricing with special discounts for bulk orders',
              'Fast delivery across all areas of Bali within 1-2 business days',
              'Consultation on the right dosage and type of chemicals',
              'Neat and safe packaging to prevent spills during delivery',
              'Complete monthly maintenance chemical bundles available',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-tertiary font-bold mt-0.5">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section className="bg-tertiary text-white py-16 px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">
              Order Pool Chemicals in Bali Now
            </h2>
            <p className="text-primary/80 mb-8">
              Get the best deal for your pool chemical needs. Contact us via
              WhatsApp for a consultation, stock availability check, and the
              best price. Our team is ready to help you choose the most suitable
              products for your needs and budget.
            </p>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary hover:opacity-90 text-tertiary font-semibold py-3 px-10 rounded-full text-lg transition"
            >
              Chat WhatsApp Now
            </a>
          </div>
        </section>

        {/* Related Services */}
        <section className="max-w-4xl mx-auto py-10 px-6">
          <h2 className="text-lg font-bold text-tertiary mb-4">Layanan Lainnya</h2>
          <div className="flex flex-wrap gap-3">
            {relatedServices.map(({ label, to }) => (
              <Link key={to} to={to} className="text-sm font-medium text-primary border border-primary/40 px-4 py-2 rounded-full hover:bg-primary/10 transition">
                {label}
              </Link>
            ))}
          </div>
        </section>

        <FooterSection />
      </main>
    </>
  );
};

export default JualChemicalPool;
